
import {
  Component,
  ChangeDetectorRef,
  OnInit,
  inject
} from '@angular/core';

import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators
} from '@angular/forms';

import { finalize } from 'rxjs';
import Swal from 'sweetalert2';
import {
  RegraMinimoNoites,
  RegraMinimoNoitesRequest,
  RegrasMinimoNoitesService
} from '../../../service/RegraMinimoNoites.service';



@Component({
  selector: 'app-regras-minimo-noites',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './regras-minimo-noites.component.html',
  styleUrl: './regras-minimo-noites.component.css'
})
export class RegrasMinimoNoitesComponent implements OnInit {

  private readonly service = inject(RegrasMinimoNoitesService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  regras: RegraMinimoNoites[] = [];

  // IDs cadastrados no backend.
  quartos = [
    { id: 1, nome: 'Cabana Ypê' },
    { id: 2, nome: 'Chalé Santorini' }
  ];

  carregando = false;
  salvando = false;
  mostrarFormulario = false;
  editandoId: number | null = null;

  filtroQuarto = 0;

  form = this.fb.nonNullable.group({
    quartoId: [1, [Validators.required]],
    data: ['', [Validators.required]],
    minimoNoites: [
      1,
      [Validators.required, Validators.min(1)]
    ],
    ativo: [true]
  });

  ngOnInit(): void {
    this.carregar();
  }

  get regrasFiltradas(): RegraMinimoNoites[] {
    if (!this.filtroQuarto) {
      return this.regras;
    }

    return this.regras.filter(
      r => r.quartoId === this.filtroQuarto
    );
  }

  carregar(): void {
    this.carregando = true;

    this.service.listar()
      .pipe(finalize(() => {
        this.carregando = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: dados => {
          this.regras = dados;
          this.cdr.detectChanges();
        },
        error: err => this.mostrarErro(
          err,
          'Não foi possível carregar as regras.'
        )
      });
  }

  novo(): void {
    this.editandoId = null;

    this.form.reset({
      quartoId: this.filtroQuarto || 1,
      data: '',
      minimoNoites: 1,
      ativo: true
    });

    this.mostrarFormulario = true;
    this.cdr.detectChanges();
  }

  editar(regra: RegraMinimoNoites): void {
    this.editandoId = regra.id;

    this.form.reset({
      quartoId: regra.quartoId,
      data: regra.data,
      minimoNoites: regra.minimoNoites,
      ativo: regra.ativo
    });

    this.mostrarFormulario = true;
    this.cdr.detectChanges();
  }

  cancelar(): void {
    this.mostrarFormulario = false;
    this.editandoId = null;
    this.form.reset();
    this.cdr.detectChanges();
  }

  salvar(): void {
    if (this.form.invalid || this.salvando) {
      this.form.markAllAsTouched();
      return;
    }

    const dados: RegraMinimoNoitesRequest =
      this.form.getRawValue();

    const editando = this.editandoId !== null;

    this.salvando = true;

    const requisicao = editando
      ? this.service.atualizar(this.editandoId!, dados)
      : this.service.criar(dados);

    requisicao
      .pipe(finalize(() => {
        this.salvando = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: () => {
          this.cancelar();
          this.carregar();

          Swal.fire({
            icon: 'success',
            title: editando
              ? 'Regra atualizada!'
              : 'Regra cadastrada!',
            text: 'As configurações foram salvas.',
            confirmButtonColor: '#696A49'
          });
        },
        error: err => this.mostrarErro(
          err,
          'Não foi possível salvar a regra.'
        )
      });
  }

  async alterarStatus(regra: RegraMinimoNoites): Promise<void> {
    const novoStatus = !regra.ativo;

    const resultado = await Swal.fire({
      title: novoStatus
        ? 'Ativar regra?'
        : 'Desativar regra?',
      text: novoStatus
        ? 'A regra voltará a ser aplicada nas reservas.'
        : 'A regra deixará de ser aplicada nas novas reservas.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#696A49',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar'
    });

    if (!resultado.isConfirmed) return;

    this.service.alterarStatus(regra.id, novoStatus)
      .subscribe({
        next: () => {
          this.carregar();

          Swal.fire({
            icon: 'success',
            title: 'Status atualizado!',
            timer: 1500,
            showConfirmButton: false
          });
        },
        error: err => this.mostrarErro(
          err,
          'Não foi possível alterar o status.'
        )
      });
  }

  async excluir(regra: RegraMinimoNoites): Promise<void> {
    const resultado = await Swal.fire({
      title: 'Excluir regra?',
      text: `A regra de ${regra.minimoNoites} noite(s) para ${this.formatarData(regra.data)} será excluída.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#b91c1c',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    });

    if (!resultado.isConfirmed) return;

    this.service.excluir(regra.id)
      .subscribe({
        next: () => {
          this.carregar();

          Swal.fire({
            icon: 'success',
            title: 'Regra excluída!',
            timer: 1500,
            showConfirmButton: false
          });
        },
        error: err => this.mostrarErro(
          err,
          'Não foi possível excluir a regra.'
        )
      });
  }

  formatarData(data: string): string {
    if (!data) return '-';

    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  calcularCheckout(data: string, noites: number): string {
    if (!data || !noites) return '-';

    const [ano, mes, dia] = data.split('-').map(Number);

    const checkout = new Date(
      Date.UTC(ano, mes - 1, dia + Number(noites))
    );

    return checkout.toLocaleDateString('pt-BR', {
      timeZone: 'UTC'
    });
  }

  private mostrarErro(err: any, padrao: string): void {
    const resposta = err?.error;

    const mensagem =
      typeof resposta === 'string'
        ? resposta
        : resposta?.mensagem ||
          resposta?.message ||
          padrao;

    Swal.fire({
      icon: 'error',
      title: 'Não foi possível concluir',
      text: mensagem,
      confirmButtonColor: '#696A49'
    });
  }
}
